import type { GameConfig, ScoreScale, BBox, Place } from "../../shared/game.ts";
import type { GameController, Player, RoundResult, RoundView } from "./game.ts";

export const API_URL: string = import.meta.env.VITE_API_URL || "https://geopin-ws-staging.webmaster7049.workers.dev";
export const WS_URL = API_URL.replace(/^http/, "ws");

/** Room state as broadcast by the server (see server/src/room.ts). */
export interface RoomState {
  phase: "lobby" | "playing" | "reveal" | "ended";
  code: string;
  hostId: string | null;
  config: GameConfig;
  maxPlayers: number;
  playerCount: number;
  guessedCount: number;
  players: Player[];
  round: number;
  totalRounds: number;
  place: (Pick<Place, "id" | "n" | "t" | "c" | "r"> & { lat?: number; lng?: number }) | null;
  endsAt: number;
  nextAt: number;
  scale: ScoreScale;
  bbox: BBox;
  history: { placeId: string; n: string; lat: number; lng: number;
    results: Record<string, { guess: { lat: number; lng: number } | null; distanceKm: number; points: number; bonus: number; hint: boolean }> }[];
}

export interface OpenRoom { code: string; hostName: string; players: number; maxPlayers: number; summary: string }

export async function createRoom(hostName: string): Promise<string> {
  const r = await fetch(`${API_URL}/create?host=${encodeURIComponent(hostName)}`, { method: "POST" });
  if (!r.ok) throw new Error("Couldn’t create a room. Try again.");
  return (await r.json()).code;
}

export async function listRooms(): Promise<OpenRoom[]> {
  try { const r = await fetch(`${API_URL}/lobbies`); return r.ok ? await r.json() : []; } catch { return []; }
}

export class MpGame implements GameController {
  readonly multiplayer = true;
  state: RoomState | null = null;
  you = "";
  private ws: WebSocket | null = null;
  private offset = 0;              // serverNow - Date.now()
  private listeners: (() => void)[] = [];
  private closedByUs = false;
  private retries = 0;
  onEvent: (text: string) => void = () => {};
  onKicked: () => void = () => {};
  onLost: () => void = () => {};

  constructor(readonly code: string, private name: string, private color: string) { this.connect(); }

  private connect() {
    const sid = sessionKey(this.code);
    const ws = new WebSocket(`${WS_URL}/room/${encodeURIComponent(this.code)}?sid=${encodeURIComponent(sid)}`);
    this.ws = ws;
    ws.onopen = () => { this.retries = 0; this.send({ type: "join", name: this.name, color: this.color }); };
    ws.onmessage = ev => {
      let m: any; try { m = JSON.parse(ev.data); } catch { return; }
      if (m.type === "state") {
        this.state = m.state; this.you = m.you ?? this.you;
        if (typeof m.serverNow === "number") this.offset = m.serverNow - Date.now();
        this.listeners.forEach(f => f());
      } else if (m.type === "event") this.onEvent(m.text);
      else if (m.type === "kicked") { this.closedByUs = true; this.onKicked(); }
      else if (m.type === "error") this.onEvent(m.message);
    };
    ws.onclose = () => {
      if (this.closedByUs) return;
      // Reconnect with the same session id; the server restores this player's seat and score.
      if (this.retries < 6) { const wait = 500 * 2 ** this.retries++; setTimeout(() => this.connect(), wait); this.onEvent("Connection dropped, reconnecting…"); }
      else this.onLost();
    };
  }

  send(msg: unknown) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg)); }
  onChange(fn: () => void) { this.listeners.push(fn); }
  /** Re-runs listeners with the current state (e.g. after leaving a sub-screen). */
  refresh() { if (this.state) this.listeners.forEach(f => f()); }
  serverToLocal(t: number) { return t ? t - this.offset : 0; }
  isHost() { return !!this.state && this.state.hostId === this.you; }

  updateConfig(config: GameConfig) { this.send({ type: "config", config }); }
  start() { this.send({ type: "start" }); }
  kick(id: string) { this.send({ type: "kick", id }); }
  playAgain() { this.send({ type: "play_again" }); }
  endEarly() { this.send({ type: "end_game" }); }

  guess(lat: number, lng: number, usedHint: boolean) { this.send({ type: "guess", lat, lng, hint: usedHint }); }
  next() { /* the server advances rounds */ }
  quit() { this.closedByUs = true; this.ws?.close(); }

  view(): RoundView {
    const s = this.state!;
    return {
      phase: s.phase === "lobby" ? "ended" : s.phase, round: s.round, totalRounds: s.totalRounds,
      place: s.place ?? { id: "", n: "", t: "city", c: "" },
      endsAt: this.serverToLocal(s.endsAt), timerSec: s.config.timer, nextAt: this.serverToLocal(s.nextAt),
      scale: s.scale, bbox: s.bbox, players: s.players, me: this.you, playerCount: s.playerCount, guessedCount: s.guessedCount,
    };
  }

  results(): RoundResult[] {
    return (this.state?.history ?? []).map(h => {
      const r = h.results[this.you];
      return { placeId: h.placeId, name: h.n, distanceKm: r?.distanceKm ?? -1, points: r?.points ?? 0, bonus: r?.bonus ?? 0,
        hint: !!r?.hint, guess: r?.guess ?? null, answer: { lat: h.lat, lng: h.lng }, ms: 0 };
    });
  }
}

function sessionKey(code: string): string {
  const k = `geopin-sid-${code}`;
  try {
    let v = sessionStorage.getItem(k);
    if (!v) { v = crypto.randomUUID(); sessionStorage.setItem(k, v); }
    return v;
  } catch { return crypto.randomUUID(); }
}
